import { Alert } from 'antd';
import { ScopeOverviewResponse } from '@ydistri/api-sdk';
import { ActionsBar, Section, Spinner } from '@ydistri/ds';
import React, { useMemo, useState } from 'react';
import CreateIconButton from '../../../../components/buttons/CreateIconButton';
import { getDisplayedScopes, ScopeCatalogSortField } from './scopeCatalogFilters';
import ScopesFilters from './components/ScopesFilters';
import ScopesList from './ScopesList';

interface ScopeCatalogProps {
  data: ScopeOverviewResponse[];
  isFetching: boolean;
  onCreate: () => void;
  selectedScopeId?: number;
  selectionUrl: (scopeId: number) => string;
}

const ScopeCatalog: React.FC<ScopeCatalogProps> = ({
  data,
  isFetching,
  onCreate,
  selectedScopeId,
  selectionUrl,
}) => {
  const [search, setSearch] = useState('');
  const [showArchivedItems, setShowArchivedItems] = useState(false);
  const [showLocalItems, setShowLocalItems] = useState(false);
  const [sortField, setSortField] = useState<ScopeCatalogSortField>('LastUpdatedAt');

  const displayedData = useMemo(
    () => getDisplayedScopes(data, { search, showArchivedItems, showLocalItems, sortField }),
    [data, search, showArchivedItems, showLocalItems, sortField],
  );
  const archivedItemsCount = useMemo(() => data.filter(scope => scope.isArchived).length, [data]);

  let listContent: React.ReactNode;
  if (isFetching) {
    listContent = <Spinner />;
  } else if (displayedData.length > 0) {
    listContent = (
      <ScopesList
        data={displayedData}
        selectedScopeId={selectedScopeId}
        selectionUrl={selectionUrl}
      />
    );
  } else if (data.length > 0) {
    listContent = (
      <Alert
        message="No Scope matches the selected filters. Please adjust the filters."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <Section
      header="Scopes"
      headerActions={
        <ActionsBar>
          <CreateIconButton size="small" tooltip="Create Scope" onClick={onCreate} />
        </ActionsBar>
      }
      $scrollableContent
      $ratio={0}
      $minWidth="44rem"
      $contentMarginTop={false}
    >
      <ScopesFilters
        archivedItemsCount={archivedItemsCount}
        search={search}
        showArchivedItems={showArchivedItems}
        showLocalItems={showLocalItems}
        sortField={sortField}
        onSearchChange={setSearch}
        onShowArchivedItemsChange={setShowArchivedItems}
        onShowLocalItemsChange={setShowLocalItems}
        onSortChange={setSortField}
      />
      {listContent}
    </Section>
  );
};

export default ScopeCatalog;
